import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

export interface OrganizationMember {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  profiles?: {
    display_name: string | null;
    email: string | null;
  } | null;
}

// Query key factory
export const memberKeys = {
  all: ['organization-members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (organizationId: string) => [...memberKeys.lists(), organizationId] as const,
};

/**
 * Fetch organization members with their profile data
 */
export function useOrganizationMembers(organizationId?: string) {
  return useQuery({
    queryKey: memberKeys.list(organizationId || ''),
    queryFn: async () => {
      if (!organizationId) {
        return [];
      }

      // Fetch members
      const { data: members, error: membersError } = await supabase
        .from('organization_members')
        .select('id, user_id, role, created_at')
        .eq('organization_id', organizationId);

      if (membersError) throw membersError;
      if (!members || members.length === 0) return [];

      // Fetch profiles separately
      const userIds = members.map(m => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name, email')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Combine members with profiles
      return members.map(member => ({
        ...member,
        profiles: profiles?.find(p => p.user_id === member.user_id) || null
      })) as OrganizationMember[];
    },
    enabled: !!organizationId,
  });
}

/**
 * Remove a member from the organization
 */
export function useRemoveMember(organizationId: string) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberId,
      memberUserId,
      memberEmail
    }: {
      memberId: string;
      memberUserId: string;
      memberEmail: string;
    }) => {
      if (!user) throw new Error('Not authenticated');

      // Remove member
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      // Log audit trail
      await supabase
        .from('organization_audit_log')
        .insert({
          organization_id: organizationId,
          action_type: 'MEMBER_REMOVED',
          performed_by: user.id,
          target_user_id: memberUserId,
          target_email: memberEmail,
        });

      return { memberId, memberUserId, memberEmail };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.list(organizationId) });
      toast.success(t('team.toasts.memberRemoved'));
    },
    onError: (error: Error) => {
      toast.error(t('team.toasts.removeFailed'));
    },
  });
}

/**
 * Change a member's role
 */
export function useChangeRole(organizationId: string) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberId,
      memberUserId,
      newRole
    }: {
      memberId: string;
      memberUserId: string;
      newRole: string;
    }) => {
      if (!user) throw new Error('Not authenticated');

      // Update role
      const { error } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;

      // Log audit trail
      await supabase
        .from('organization_audit_log')
        .insert({
          organization_id: organizationId,
          action_type: 'ROLE_CHANGED',
          performed_by: user.id,
          target_user_id: memberUserId,
          details: { new_role: newRole }
        });

      return { memberId, newRole };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.list(organizationId) });
      toast.success(t('team.toasts.roleUpdated'));
    },
    onError: (error: Error) => {
      toast.error(t('team.toasts.roleUpdateFailed'));
    },
  });
}
